<script setup lang="ts">
import { useSiteStore } from '~/stores/site.store';
import { useAuthStore } from '~/stores/auth.store.js';
import type { IGameCategoriesV4Data } from '~/interfaces/dto/games/response';
import { useGetVertical } from '~/composables/useGaming';
import HomeVerticalSkeleton from '~/components/category/home-vertical-skeleton.vue';
import WinnersMarquee from '~/components/category/winners-marquee.vue';

const auth = useAuthStore();
const { $t } = useNuxtApp();
const runtimeConfig = useRuntimeConfig();
const site = useSiteStore();
const page = useGetPage();
const { getPlaceholder } = useBranding();

const vertical = ref<IGameCategoriesV4Data>();
const loading = ref<boolean>(true);

const displayAlternateLanding = computed(() => {
  return (
    !auth.loggedIn &&
    (site.getFeatureFlags.get('website.loggedoutlandingpage') || false)
  );
});

const displayMasonry = computed(
  () => site.getFeatureFlags.get('website.displaymasonry') || false,
);

const enableHowToBanners = computed(() => {
  return site.getFeatureFlags.get('website.enablehowtobanners')
    ? site.getFeatureFlags.get('website.enablehowtobanners')
    : false;
});

const categoryRoute = computed(() => (categoryName: string, alias: string) => {
  const categoryNameUrl = useGetPage({ path: categoryName, setSeoMeta: false });
  if (!!categoryNameUrl.value) return `/${categoryName}`;
  return `/${alias}`;
});

const fetchVertical = async () => {
  try {
    vertical.value = await useGetVertical(true);
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await fetchVertical();
});

watch(
  () => [site.getEnvironment, site.getRegionCode, page.value?.Mapping],
  async (val, oldVal) => {
    if (val !== oldVal && import.meta.dev) {
      await fetchVertical();
    }
  },
  { deep: true, immediate: true },
);
</script>
<template>
  <div>
    <LazyMarketingBanner
      v-if="String(runtimeConfig.public.newBannerWidget) !== 'true'"
      display-placeholder
      :vertical="page?.Mapping"
      :desktop-aspect-ratio="displayAlternateLanding ? '16/3' : '16/5'"
      :mobile-aspect-ratio="displayAlternateLanding ? '355/104' : '344/247'"
      :desktop-placeholder="getPlaceholder('marketingPlaceholderDesktop')"
      :mobile-placeholder="getPlaceholder('marketingPlaceholderMobile')"
    />
    <LazyMarketingBannerV2
      v-else-if="String(runtimeConfig.public.newBannerWidget) === 'true'"
      :vertical="page?.Mapping"
      :desktop-aspect-ratio="displayAlternateLanding ? '16/3' : '16/5'"
      :mobile-aspect-ratio="displayAlternateLanding ? '355/104' : '344/247'"
      :desktop-placeholder="getPlaceholder('marketingPlaceholderDesktop')"
      :mobile-placeholder="getPlaceholder('marketingPlaceholderMobile')"
      :autoplay="5000"
    />
    <div class="container">
      <div v-if="enableHowToBanners">
        <kentico-shadow
          key="how-to"
          content="/slot-lobby"
          :terminate-to-error="false"
          :seo-content="false"
        />
      </div>

      <div v-if="displayAlternateLanding">
        <kentico-shadow
          key="landing"
          content="/landing"
          :terminate-to-error="false"
          :seo-content="false"
        />
      </div>

      <div v-else>
        <WinnersMarquee />
        <LazyCategoryCarouselFavorites />
        <LazyCategoryCarouselRecentlyPlayed />
        <HomeVerticalSkeleton v-show="loading" id="home-vertical-skeleton" />
        <div v-show="!loading" id="home-vertical">
          <div
            v-if="vertical?.categories?.length"
            id="home-vertical-categories"
          >
            <div
              v-for="(category, index) in vertical?.categories"
              :key="index"
              :id="`category-${index}`"
            >
              <LazyCategoryProvidersCarousel
                v-if="!!vertical?.providers && index === 1"
                :providers="vertical?.providers"
                :vertical="page?.Mapping"
              />
              <CategoryMasonrySimple
                v-if="
                  (category.name === 'featured' ||
                    index === vertical.categories.length - 1) &&
                  displayMasonry
                "
                :games="category.games"
                :title="
                  $t(
                    `vertical.category.${category.name}-${site.getRegionCode}`,
                    `vertical.category.${category.name}`,
                  )
                "
                :alias="category.alias"
              >
                <template #subtitle>
                  <div class="font-normal hidden md:block">
                    &nbsp;|
                    {{ $t(`vertical.category.${category.name}.description`) }}
                  </div>
                </template>
              </CategoryMasonrySimple>
              <CategoryCarousel
                v-else-if="
                  (category.name === 'featured' ||
                    index === vertical.categories.length - 1) &&
                  !displayMasonry
                "
                :games="category.games"
                :alias="category.alias"
                :title="
                  $t(
                    `vertical.category.${category.name}-${site.getRegionCode}`,
                    `vertical.category.${category.name}`,
                  )
                "
                :vertical-navigation="true"
                title-class="text-base"
                :vertical-tile="true"
              >
                <template #subtitle>
                  <div class="font-normal hidden md:block">
                    &nbsp;|
                    {{ $t(`vertical.category.${category.name}.description`) }}
                  </div>
                </template>
              </CategoryCarousel>
              <CategoryCarousel
                v-else
                :games="category.games"
                :title="
                  $t(
                    `vertical.category.${category.name}-${site.getRegionCode}`,
                    `vertical.category.${category.name}`,
                  )
                "
                :route="categoryRoute(category.name, category.alias)"
                :vertical-navigation="true"
                title-class="text-base"
                :alias="category.alias"
                :total-games="category.totalGames"
              >
                <template #subtitle>
                  <div class="font-normal hidden md:block">
                    &nbsp;|
                    {{ $t(`vertical.category.${category.name}.description`) }}
                  </div>
                </template>
              </CategoryCarousel>
            </div>
          </div>
          <CategoryError v-else-if="!vertical?.categories?.length" />
        </div>
      </div>
      <LazyContentSeo content="/" />
    </div>
  </div>
</template>
